import { differenceInCalendarDays, format, parseISO } from 'date-fns';

import { Api } from '../api';
import { formatTenths, zeroPad } from '../util';

/**
 * The length of the billable day a service order's day limit counts in.
 */
export const MINUTES_PER_DAY = 6 * 60;

type Stage = {
  name: string;
  startDate: string | null;
  estimatedFinishDate: string | null;
  finishDate: string | null;
};

function formatDay(date: string): string {
  return format(parseISO(date), 'd MMM');
}

/**
 * A stage as the Projects summary carries it, which the detail screen is
 * built on: everything the order's own read gives, plus the time logged.
 */
export type ServiceOrderSummary =
  Api.CompanyProjectSummary.V['serviceOrders'][number];

export type ProjectSummary =
  Api.CompanyProjectSummary.V['projects'][number]['project'];

/**
 * A service order with the client it belongs to and, where it has one, the
 * project it forms part of.  The detail endpoint does not carry the order's
 * names or the time logged against it, so the summary is the authoritative
 * read the Service Order screen is driven from.
 */
export type ServiceOrderWithContext = {
  company: Api.Company.BaseView;
  project: ProjectSummary | null;
  serviceOrder: ServiceOrderSummary;
};

export function findServiceOrder(
  summaries: ReadonlyArray<Api.CompanyProjectSummary.V>,
  serviceOrderId: string
): ServiceOrderWithContext | null {
  for (const summary of summaries) {
    for (const { project, serviceOrders } of summary.projects) {
      const serviceOrder = serviceOrders.find(
        (candidate) => candidate.id === serviceOrderId
      );
      if (serviceOrder) {
        return { company: summary.company, project, serviceOrder };
      }
    }
    const serviceOrder = summary.serviceOrders.find(
      (candidate) => candidate.id === serviceOrderId
    );
    if (serviceOrder) {
      return { company: summary.company, project: null, serviceOrder };
    }
  }

  return null;
}

/**
 * Time logged against a service order, in the days its limit is counted in
 * and then the clock hours, e.g. "2.5 days · 15h 00m".
 */
export function formatTimeLogged(totalMinutesLogged: number): string {
  const h = Math.floor(totalMinutesLogged / 60);
  const m = Math.round(totalMinutesLogged % 60);
  return `${formatDays(getDaysUsed(totalMinutesLogged))} days · ${h}h ${zeroPad(
    m
  )}m`;
}

/**
 * The stage the work is up to, being the first one not yet finished.
 */
export function getNextStage<T extends Stage>(
  stages: ReadonlyArray<T>
): T | null {
  return stages.find((stage) => stage.finishDate === null) ?? null;
}

/**
 * When the work began, taken as the earliest stage to have started.  The dates
 * are ISO days, which sort correctly as plain strings.
 */
function getStartDate(stages: ReadonlyArray<Stage>): string | null {
  const started = stages
    .map((stage) => stage.startDate)
    .filter((date): date is string => date !== null)
    .sort();

  return started[0] ?? null;
}

/**
 * Past this much of the span between starting and the stage being due, the
 * dates want attention rather than just reading as time passing.
 */
export const DUE_SOON_AT = 0.75;

export type DateTrack = {
  /** How far along the span today is, over 1 once the due date has gone. */
  elapsed: number;
  tone: 'neutral' | 'warning' | 'danger';
  startLabel: string;
  dueLabel: string;
  /**
   * Only set where there is something to count, so work still comfortably in
   * hand reads as a plain pair of dates.
   */
  countdown: string | null;
};

function formatDaysAway(days: number): string {
  return `${days} ${1 === days ? 'day' : 'days'}`;
}

/**
 * A service order's dates as the calendar strip draws them: from when the work
 * began to when the stage in flight is due.  Null where either end is unknown,
 * or where the two land on the same day and there is no span to draw.
 */
export function getDateTrack<T extends Stage>(
  stages: ReadonlyArray<T>,
  today: Date
): DateTrack | null {
  const startDate = getStartDate(stages);
  const dueDate = getNextStage(stages)?.estimatedFinishDate ?? null;

  if (null === startDate || null === dueDate) {
    return null;
  }

  const due = parseISO(dueDate);
  const span = differenceInCalendarDays(due, parseISO(startDate));

  if (span <= 0) {
    return null;
  }

  // Clamped at the start so work logged before its own start date, which the
  // API does not prevent, does not draw a bar running backwards.
  const elapsed = Math.max(
    differenceInCalendarDays(today, parseISO(startDate)) / span,
    0
  );
  const daysLeft = differenceInCalendarDays(due, today);

  return {
    elapsed,
    tone:
      elapsed > 1 ? 'danger' : elapsed >= DUE_SOON_AT ? 'warning' : 'neutral',
    startLabel: formatDay(startDate),
    dueLabel: formatDay(dueDate),
    countdown:
      daysLeft < 0
        ? `${formatDaysAway(-daysLeft)} over`
        : 0 === daysLeft
        ? 'due today'
        : elapsed >= DUE_SOON_AT
        ? `${formatDaysAway(daysLeft)} left`
        : null,
  };
}

/**
 * The day limit, which the API sends as a decimal string.
 */
export function getDayLimit(serviceOrder: {
  dayLimit: string | null;
}): number | null {
  if (serviceOrder.dayLimit === null) return null;
  const parsed = Number(serviceOrder.dayLimit);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Time logged against the work, in the days its limit is counted in.
 */
export function getDaysUsed(totalMinutesLogged: number): number {
  return totalMinutesLogged / MINUTES_PER_DAY;
}

/**
 * How far through the day limit the work is, capped at 1.
 */
export function getDaysProgress(used: number, limit: number): number {
  return limit > 0 ? Math.min(used / limit, 1) : 0;
}

/**
 * Days as they read beside the bar, keeping a part-day rather than hiding it.
 */
export function formatDays(days: number): string {
  return formatTenths(days);
}

type DayLimitProgress = {
  progress: number;
  label: string;
};

/**
 * The day limit and how much of it has gone, or null for work not bought by
 * the day.  Every agency user sees this, unlike the fee.
 */
export function getDayLimitProgress(
  serviceOrder: { dayLimit: string | null },
  totalMinutesLogged: number
): DayLimitProgress | null {
  const limit = getDayLimit(serviceOrder);

  if (null === limit || limit <= 0) {
    return null;
  }

  const used = getDaysUsed(totalMinutesLogged);

  return {
    progress: getDaysProgress(used, limit),
    label: `${formatDays(used)} of ${formatDays(limit)} days`,
  };
}

/**
 * The fee, which the API nulls for anyone but an agency owner.
 */
export function formatFee(budget: number | null): string | null {
  return null === budget ? null : `£${budget.toLocaleString('en-GB')}`;
}
